import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Bar, BarChart, XAxis, YAxis } from "recharts"

import type { DashboardUseCase } from "../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { STATUS_COLORS } from "../../types/use-case.types"

const BUCKETS = ["< 1 week", "< 1 month", "1-3 months", "3-6 months"] as const
const STATUS_NAMES = ["Implemented", "In Progress", "Planned", "On Hold"] as const

type ImplTimelineChartProps = {
  useCases: DashboardUseCase[]
}

const STATUS_LABEL_KEYS: Record<(typeof STATUS_NAMES)[number], string> = {
  Implemented: "useCases.factSheet.status.implemented",
  "In Progress": "useCases.factSheet.status.inProgress",
  Planned: "useCases.factSheet.status.planned",
  "On Hold": "useCases.factSheet.status.onHold",
}

export function ImplTimelineChart({ useCases }: ImplTimelineChartProps) {
  const { t } = useTranslation()

  const chartConfig = useMemo(
    () =>
      ({
        Implemented: {
          label: t(STATUS_LABEL_KEYS.Implemented),
          color: STATUS_COLORS.Implemented,
        },
        "In Progress": {
          label: t(STATUS_LABEL_KEYS["In Progress"]),
          color: STATUS_COLORS["In Progress"],
        },
        Planned: { label: t(STATUS_LABEL_KEYS.Planned), color: STATUS_COLORS.Planned },
        "On Hold": { label: t(STATUS_LABEL_KEYS["On Hold"]), color: STATUS_COLORS["On Hold"] },
      }) satisfies ChartConfig,
    [t]
  )

  const data = useMemo(() => {
    const rows = BUCKETS.map((bucket) => {
      const row: Record<string, string | number> = { bucket }
      for (const status of STATUS_NAMES) {
        row[status] = 0
      }
      return row
    })

    for (const uc of useCases) {
      const idx = BUCKETS.indexOf(uc.implTime as (typeof BUCKETS)[number])
      if (idx === -1) continue
      const status = uc.statusText
      if (status in rows[idx]) {
        rows[idx][status] = (rows[idx][status] as number) + 1
      }
    }

    return rows
  }, [useCases])

  const hasData = data.some((row) => STATUS_NAMES.some((s) => (row[s] as number) > 0))

  if (!hasData) return null

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("useCases.dashboard.charts.implTimeline")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer className="h-[240px] w-full" config={chartConfig}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 10, right: 30, top: 5, bottom: 5 }}
          >
            <XAxis tick={{ fontSize: 11 }} type="number" />
            <YAxis dataKey="bucket" tick={{ fontSize: 11 }} type="category" width={90} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            {STATUS_NAMES.map((status) => (
              <Bar
                key={status}
                dataKey={status}
                fill={STATUS_COLORS[status]}
                stackId="stack"
                stroke="hsl(var(--foreground))"
                strokeWidth={1}
              />
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
